import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { ToastContainer, useToast, ConfirmDialog, useConfirm } from './ui';
import BadgeList   from './Tabs/BadgeList';
import BadgeForm   from './Tabs/BadgeForm';

// Inject REST nonce
apiFetch.use(apiFetch.createNonceMiddleware(window.PBFW_SETTINGS?.nonce || ''));

const App = () => {
    // 'list' | 'new' | 'edit'
    const [view, setView]       = useState('list');
    const [editId, setEditId]   = useState(null);
    const [badges, setBadges]   = useState([]);
    const [loading, setLoading] = useState(false);

    const { toasts, toast, removeToast }            = useToast();
    const { dialog, confirm, handleConfirm, handleCancel } = useConfirm();

    const fetchBadges = () => {
        setLoading(true);
        apiFetch({ path: `pbfw/v1/badges?nocache=${Date.now()}` })
            .then(data => { setBadges(data); setLoading(false); })
            .catch(() => { setLoading(false); });
    };

    useEffect(() => { fetchBadges(); }, []);

    const handleNew  = ()    => { setEditId(null); setView('new');  };
    const handleEdit = (id)  => { setEditId(id);   setView('edit'); };
    const handleBack = ()    => { setEditId(null); setView('list'); fetchBadges(); };

    const handleDelete = async (id) => {
        const ok = await confirm({
            title: 'Delete this badge?',
            message: 'This badge will be permanently removed and will no longer appear on products.',
            confirmLabel: 'Delete',
            type: 'danger',
        });
        if (!ok) return;
        setLoading(true);
        apiFetch({ path: `pbfw/v1/badge/${id}?nocache=${Date.now()}`, method: 'DELETE' })
            .then(() => { toast.success('Badge deleted.'); fetchBadges(); })
            .catch(() => { toast.error('Failed to delete badge.'); setLoading(false); });
    };

    return (
        <div id="pbfw-admin-root">
            <ToastContainer toasts={toasts} onRemove={removeToast} />
            <ConfirmDialog dialog={dialog} onConfirm={handleConfirm} onCancel={handleCancel} />

            <div className="cpiw-page">
                {/* Header */}
                <div className="cpiw-page-header">
                    <div className="cpiw-page-header__left">
                        <div>
                            <h1 className="cpiw-page-header__title">Product Badges For WooCommerce</h1>
                            <p className="cpiw-page-header__sub">Create and manage WooCommerce product badges</p>
                        </div>
                    </div>
                    <div className="cpiw-page-header__actions">
                        {view !== 'list' ? (
                            <button className="cpiw-btn-white" onClick={handleBack}>← Back to Badges</button>
                        ) : (
                            <button className="cpiw-btn-white" onClick={handleNew}>+ New Badge</button>
                        )}
                        <a href="https://www.plugin999.com/support/" target="_blank" rel="noreferrer" className="cpiw-btn-white">
                            &#128172; Support
                        </a>
                         <a href="https://www.plugin999.com/plugin/product-badges-for-woocommerce/" target="_blank" rel="noreferrer" className="cpiw-btn-secondary">
                            &#128172; Get Pro Version
                        </a>
                    </div>
                </div>

                {/* Loader overlay — only on list view; form manages its own */}
                {loading && view === 'list' && (
                    <div className="cpiw-overlay-loader">
                        <div className="cpiw-spinner-wrap">
                            <div className="cpiw-spinner" />
                            <span className="cpiw-spinner-label">Loading…</span>
                        </div>
                    </div>
                )}

                {/* Views */}
                {view === 'list' && (
                    <BadgeList
                        badges={badges}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onNew={handleNew}
                    />
                )}
                {(view === 'new' || view === 'edit') && (
                    <BadgeForm
                        editId={editId}
                        toast={toast}
                        onSaved={handleBack}
                        onCancel={handleBack}
                    />
                )}
            </div>
        </div>
    );
};

export default App;
